/**
 * Look up aircraft metadata (type, registration, manufacturer) by ICAO 24-bit
 * hex transponder code via the pifeeder-api `/api/aircraft/:hex` endpoint.
 *
 * Upstream data source: tar1090-db (https://github.com/wiedehopf/tar1090-db)
 * which republishes the Mictronics aircraft database
 * (https://www.mictronics.de/aircraft-database/) under the
 * Open Data Commons Attribution License (ODC-BY 1.0).
 *
 * The attribution is rendered in the SPA's UI (see `src/ui/legend.ts` /
 * `src/ui/header.ts`) and surfaced by the API in the `attribution` field
 * + `X-Data-Attribution` header.
 *
 * Caching: in-memory map for the session + localStorage for cross-reload
 * persistence. Misses (404 / network errors) are cached with a shorter TTL
 * so we don't replay the same dead lookups every poll.
 */

import { apiBase } from "./api";

export interface AircraftInfo {
  type?: string;
  manufacturer?: string;
  registration?: string;
  operator?: string;
}

const HIT_TTL_MS = 7 * 24 * 60 * 60_000;
const MISS_TTL_MS = 60 * 60_000;
const STORAGE_KEY = "pifeeder-hex-cache-v2";
// LRU cap to prevent localStorage quota exhaustion. A busy session can
// see >10k unique hexes in a few hours; at ~120 bytes per entry that's
// ~1.2 MB — fine — but compounded over weeks the cache grew unbounded
// and would silently start failing writes (browsers throw QuotaExceeded
// and `saveDiskEntry`'s try/catch swallowed it, leaving cache
// inconsistent). Cap at 5000 entries (~600 KB) — well under the
// per-origin localStorage budget on every browser. Eviction is by
// timestamp ascending (= oldest first). See SCALING_AUDIT.md S-08.
const DISK_CACHE_MAX_ENTRIES = 5000;
const DISK_CACHE_EVICT_TARGET = 4500; // evict down to this to avoid churn

interface CacheEntry {
  info: AircraftInfo | null;
  ts: number;
}

const memCache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<AircraftInfo | null>>();

function loadDiskCache(): Record<string, CacheEntry> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, CacheEntry>) : {};
  } catch {
    return {};
  }
}

function saveDiskEntry(hex: string, entry: CacheEntry) {
  try {
    const all = loadDiskCache();
    all[hex] = entry;
    // LRU prune: if the cache has grown past the cap, drop the oldest
    // entries (by ts) until we're under DISK_CACHE_EVICT_TARGET.
    const keys = Object.keys(all);
    if (keys.length > DISK_CACHE_MAX_ENTRIES) {
      keys.sort((a, b) => all[a]!.ts - all[b]!.ts);
      const toEvict = keys.length - DISK_CACHE_EVICT_TARGET;
      for (let i = 0; i < toEvict; i++) {
        delete all[keys[i]!];
      }
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    // localStorage unavailable / quota (the latter shouldn't happen
    // post-LRU, but a private-mode browser can still return 0 quota).
  }
}

function fresh(entry: CacheEntry): boolean {
  const ttl = entry.info ? HIT_TTL_MS : MISS_TTL_MS;
  return Date.now() - entry.ts < ttl;
}

/** Server response shape from `/api/aircraft/:hex`. */
interface ApiAircraftResponse {
  icao24: string;
  type?: string | null;
  registration?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  owner?: string | null;
}

export function getCachedAircraft(hex: string): AircraftInfo | null {
  const key = hex.toLowerCase();
  const mem = memCache.get(key);
  if (mem && fresh(mem)) return mem.info;
  const disk = loadDiskCache()[key];
  if (disk && fresh(disk)) {
    memCache.set(key, disk);
    return disk.info;
  }
  return null;
}

export async function lookupAircraftByHex(hex: string): Promise<AircraftInfo | null> {
  const key = hex.toLowerCase();

  const cached = getCachedAircraft(key);
  if (cached !== null) return cached;
  const memEntry = memCache.get(key);
  if (memEntry && fresh(memEntry)) return memEntry.info;

  const pending = inflight.get(key);
  if (pending) return pending;

  const base = apiBase();
  if (!base) {
    // No backend configured (mock mode) — record a short miss so we don't
    // re-attempt every tick.
    const entry: CacheEntry = { info: null, ts: Date.now() };
    memCache.set(key, entry);
    return null;
  }

  const p = (async (): Promise<AircraftInfo | null> => {
    try {
      const res = await fetch(`${base}/api/aircraft/${key}`, { cache: "no-store" });
      if (!res.ok) {
        const entry: CacheEntry = { info: null, ts: Date.now() };
        memCache.set(key, entry);
        saveDiskEntry(key, entry);
        return null;
      }
      const json = (await res.json()) as ApiAircraftResponse;
      const info: AircraftInfo = {
        type: json.type ?? undefined,
        manufacturer: json.manufacturer ?? undefined,
        registration: json.registration ?? undefined,
        operator: json.owner ?? undefined,
      };
      const entry: CacheEntry = { info, ts: Date.now() };
      memCache.set(key, entry);
      saveDiskEntry(key, entry);
      return info;
    } catch {
      const entry: CacheEntry = { info: null, ts: Date.now() };
      memCache.set(key, entry);
      return null;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, p);
  return p;
}

/** Test-only: clear all caches between runs. */
export function _resetAircraftCache() {
  memCache.clear();
  inflight.clear();
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
